<template>
    <div>
        <b-tabs
            v-model="currentSettingsTab"
            nav-wrapper-class="golia-pills-tabs"
            content-class="mt-3"
            small
            pills
            card
        >
            <b-tab lazy>
                <template #title>
                    <i class="fa-solid fa-chart-line"></i> {{ __('analisys.chart_settings') }}
                </template>

                <h6 class="font-bold">{{ __('analisys.select_telemetries') }}</h6>
                <p class="mb-4 text-smaller">
                    {{ __('analisys.select_telemetries_description') }}
                </p>

                <div class="mb-4" v-for="(groupFields, group) in chartFieldsByGroup" :key="group">
                    <h6 class="section-title mt-4 mb-2 font-bold">
                        {{ __('analisys.chart_groups.' + group) }}
                    </h6>

                    <div
                        class="bg-main-inner-bg p-3 rounded-3 form-group d-flex align-items-center flex-wrap gap-3"
                    >
                        <div
                            class="form-check"
                            style="flex-basis: 45%; flex-wrap: wrap"
                            v-for="(field, fieldName) in groupFields"
                            :key="fieldName"
                        >
                            <input
                                class="form-check-input"
                                type="checkbox"
                                v-model="field.active"
                                :disabled="isChartFieldLocked(field, fieldName)"
                                :id="'chart-' + fieldName"
                            />
                            <label class="form-check-label" :for="'chart-' + fieldName">
                                {{ __(fieldName) }}
                            </label>

                            <!-- `activity` can be read either as the raw telemetry or as
                                 the computed driver activity. The choice rides along in
                                 `extension.computed`. -->
                            <div
                                v-if="fieldName == 'activity'"
                                style="width: 100%; padding-top: 6px"
                            >
                                <toggle-button
                                    v-model="field.extension.computed"
                                    :sync="true"
                                    :disabled="!field.active"
                                    class="me-3"
                                />
                                <label
                                    class="form-check-label"
                                    :style="{ opacity: field.active ? 1 : 0.7 }"
                                >
                                    {{
                                        field.extension.computed
                                            ? __('driver_activity')
                                            : __('telemetry')
                                    }}
                                </label>
                            </div>
                        </div>
                    </div>
                </div>
            </b-tab>

            <b-tab lazy>
                <template #title>
                    <i class="fa-solid fa-map-location-dot"></i> {{ __('analisys.map_settings') }}
                </template>

                <h6 class="font-bold">{{ __('analisys.select_telemetries_map') }}</h6>
                <p class="mb-4 text-smaller">
                    {{ __('analisys.select_telemetries_map_description') }}
                </p>

                <div class="mb-4" v-for="(groupFields, group) in mapFieldsByGroup" :key="group">
                    <h6 class="section-title mt-4 mb-2 font-bold">
                        {{ __('analisys.chart_groups.' + group) }}

                        <!-- The accumulated group needs explaining: its values are running
                             totals up to the clicked point, not readings taken there. -->
                        <i
                            v-if="group == 'accumulated_data'"
                            class="fa-regular fa-circle-info ms-1 text-muted"
                            id="map-accumulated-data-hint"
                        ></i>
                    </h6>

                    <b-popover
                        v-if="group == 'accumulated_data'"
                        target="map-accumulated-data-hint"
                        triggers="hover focus"
                        :placement="popoverPlacement"
                        variant="info"
                    >
                        {{ __('analisys.accumulated_data_hint') }}
                    </b-popover>

                    <div
                        class="bg-main-inner-bg p-3 rounded-3 form-group d-flex align-items-center flex-wrap gap-3"
                    >
                        <div
                            class="form-check"
                            style="flex-basis: 45%; flex-wrap: wrap"
                            v-for="(field, fieldName) in groupFields"
                            :key="fieldName"
                        >
                            <input
                                class="form-check-input"
                                type="checkbox"
                                v-model="field.active"
                                :id="'map-' + fieldName"
                            />
                            <label class="form-check-label" :for="'map-' + fieldName">
                                {{ __(fieldName) }}
                            </label>

                            <div
                                v-if="fieldName == 'activity'"
                                style="width: 100%; padding-top: 6px"
                            >
                                <toggle-button
                                    v-model="field.extension.computed"
                                    :sync="true"
                                    :disabled="!field.active"
                                    class="me-3"
                                />
                                <label
                                    class="form-check-label"
                                    :style="{ opacity: field.active ? 1 : 0.7 }"
                                >
                                    {{
                                        field.extension.computed
                                            ? __('driver_activity')
                                            : __('telemetry')
                                    }}
                                </label>
                            </div>
                        </div>
                    </div>
                </div>
            </b-tab>
        </b-tabs>

        <!-- Save/Reset apply to BOTH tabs at once: the modal is a single settings unit. -->
        <golia-button
            @click="saveSettings()"
            :label="__('save_settings')"
            variant="btn-fill success"
        />
        <golia-button
            @click="resetSettings()"
            class="float-end"
            :label="__('reset_settings')"
            variant="btn-fill dark-blue"
        />
    </div>
</template>

<script>
import cloneDeep from 'lodash/cloneDeep'

import { useAnalyzePageConfig } from '@/composables/useAnalyzePageConfig'
import { ToggleButton } from 'vue-js-toggle-button'

/** Max telemetries plottable as chart series at once (one Y axis each). */
const MAX_ACTIVE_CHART_SERIES = 4

export default {
    name: 'AnalyzePageConfig',
    components: {
        ToggleButton
    },
    setup() {
        // Two independent selections over the same telemetries: `chart` drives the
        // chart series, `mapTelemetries` drives the map popups and tooltips.
        // `defaults` is the factory value, used by resetSettings().
        const { defaults: chartDefaults, selection: chartSelection } = useAnalyzePageConfig('chart')
        const { defaults: mapDefaults, selection: mapSelection } =
            useAnalyzePageConfig('mapTelemetries')

        return {
            chartDefaults,
            chartSelection,
            mapDefaults,
            mapSelection
        }
    },
    data() {
        return {
            // Working copies, edited by the checkboxes and written back on save.
            chart: {},
            mapTelemetries: {},
            currentSettingsTab: 0
        }
    },
    created() {
        // Clone: the selections are derived state (computed), and the checkboxes edit
        // these copies in place until the user saves.
        this.chart = cloneDeep(this.chartSelection)
        this.mapTelemetries = cloneDeep(this.mapSelection)
    },
    computed: {
        // ─── Chart tab ────────────────────────────────────────────────────
        chartFieldsByGroup() {
            return this.groupFieldsByGroup(this.chart)
        },
        /** Names of the active chart fields that occupy a Y axis (`count: true`). */
        activeCountableChartFields() {
            return Object.keys(this.chart).filter(
                (fieldName) => this.chart[fieldName].active && this.chart[fieldName].count
            )
        },

        // ─── Map tab ──────────────────────────────────────────────────────
        mapFieldsByGroup() {
            return this.groupFieldsByGroup(this.mapTelemetries)
        },

        // Config Bus (Sprint 18, REFACTOR-030): rimossa mapState `config` (blob intero)
        // — era dead (mai letta). Il default del grafico è gestito da `useAnalyzePageConfig`.

        isMobile() {
            return this.$mq == 'xs' || this.$mq == 'sm'
        },
        popoverPlacement() {
            if (this.isMobile) {
                return 'top'
            }
            return 'right'
        }
    },
    methods: {
        /**
         * Buckets a selection by its `group` (ex. movement_data: { speed: {...}, altitude: {...} }),
         * so each group can be rendered under its own heading.
         */
        groupFieldsByGroup(selection) {
            return Object.keys(selection).reduce((groups, fieldName) => {
                const field = selection[fieldName]

                if (!field.group) return groups

                if (!groups[field.group]) groups[field.group] = {}

                groups[field.group] = { ...groups[field.group], [fieldName]: field }
                return groups
            }, {})
        },

        /**
         * A chart checkbox is locked when ticking it would exceed the axis budget, or
         * when unticking it would leave the chart with no series at all. Fields without
         * `count` (ex. `activity`, drawn on its own band) are never locked.
         *
         * Only the chart tab has this limit — the map popup lists every selected field.
         */
        isChartFieldLocked(field, fieldName) {
            if (!field.count) return false

            const activeCount = this.activeCountableChartFields.length
            const isCurrentlyActive = this.activeCountableChartFields.includes(fieldName)

            if (isCurrentlyActive) return activeCount == 1

            return activeCount >= MAX_ACTIVE_CHART_SERIES
        },

        /** Strips a working copy down to the only bits worth persisting. */
        serializeSelection(selection) {
            return Object.keys(selection).reduce((persisted, fieldName) => {
                persisted[fieldName] = {
                    active: selection[fieldName].active,
                    // cloneDeep: this object is about to become store state, while
                    // `this.chart` stays live under the checkboxes. Handing over the
                    // reference would leave the store holding the component's own
                    // `extension`, so every later toggle of `v-model="field.extension.computed"`
                    // would edit Vuex directly — no commitChange, no dirty snapshot, and the
                    // next debounced flush silently persists an unsaved change.
                    extension: cloneDeep(selection[fieldName].extension) || {}
                }
                return persisted
            }, {})
        },

        saveSettings() {
            // Config Bus (Sprint 18, REFACTOR-030): commitChange granulare al posto di
            // saveConfig → MERGE full-reassign → niente cascata.
            // Two commits, one debounced request: the store batches the flush.
            this.$store.dispatch('config/commitChange', {
                key: 'chart',
                value: this.serializeSelection(this.chart)
            })
            this.$store.dispatch('config/commitChange', {
                key: 'mapTelemetries',
                value: this.serializeSelection(this.mapTelemetries)
            })

            this.$emit('savedSettings')
        },

        resetSettings() {
            // cloneDeep is mandatory: `chartDefaults` is one object for this component's
            // lifetime, so assigning it raw would let the checkboxes edit it and make the
            // NEXT reset restore those edits instead of the factory values.
            this.chart = cloneDeep(this.chartDefaults)
            this.mapTelemetries = cloneDeep(this.mapDefaults)
            this.saveSettings()
        }
    }
}
</script>
